import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, FileText, Download, Lock, Users, Plus, X, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const LEVELS = [
  { value: "read", label: "Read only" },
  { value: "edit", label: "Edit (can delete)" },
  { value: "full", label: "Full control (can change access)" },
];

const DOC_TYPES = ["Business Case", "PID", "Plan", "Gate Pack", "Report", "Contract", "Other"];

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

export const Documents = ({ projectId, stages }) => {
  const [docs, setDocs] = useState([]);
  const [stage, setStage] = useState("");
  const [docType, setDocType] = useState("Gate Pack");
  const [uploading, setUploading] = useState(false);
  const [perms, setPerms] = useState(null);
  const [edit, setEdit] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(
    () => api.get(`/projects/${projectId}/documents`).then(({ data }) => setDocs(data)),
    [projectId],
  );

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    if (stage) body.append("stage", stage);
    if (docType) body.append("doc_type", docType);
    setUploading(true);
    try {
      await api.post(`/projects/${projectId}/documents`, body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/files/${d.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download that document");
    }
  };

  const savePerms = async () => {
    const allowed = (perms.allowed || []).filter((a) => a.email?.trim());
    if (perms.access === "Restricted" && allowed.length === 0) {
      return toast.error("Add at least one person, or set access back to everyone");
    }
    try {
      await api.put(`/documents/${perms.id}/permissions`, { access: perms.access, allowed });
      setPerms(null);
      await load();
      toast.success(perms.access === "Restricted" ? "Access restricted" : "Access opened to the workspace");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/documents/${edit.id}`, { stage: edit.stage || null, doc_type: edit.doc_type || null });
      setEdit(null);
      await load();
      toast.success("Document details updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.original_filename}?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      await load();
      toast.success("Document deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const grouped = docs.reduce((acc, d) => {
    const k = d.stage || "Unassigned stage";
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="documents-tab">
      <div className="panel flex flex-wrap items-end gap-4 p-5" data-testid="document-upload-panel">
        <div className="w-48">
          <label className="field-label">Stage Gate</label>
          <Select testId="document-stage-select" value={stage} onChange={setStage} options={stages || []} placeholder="Unassigned" />
        </div>
        <div className="w-48">
          <label className="field-label">Document Type</label>
          <Select testId="document-type-select" value={docType} onChange={setDocType} options={DOC_TYPES} placeholder="Select type" />
        </div>
        <div>
          <input ref={fileRef} data-testid="document-file-input" type="file" onChange={upload} className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg,.msg" />
          <Button data-testid="document-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload Document"}
          </Button>
          <p className="mt-1.5 text-[11px] text-slate-400">Office, PDF, CSV or image · max 15MB</p>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="panel px-6 py-14 text-center text-sm text-slate-500" data-testid="documents-empty">
          No documents yet. Upload gate packs and evidence against the relevant stage gate.
        </div>
      ) : (
        Object.entries(grouped).map(([stageName, list]) => (
          <section key={stageName} className="panel p-5" data-testid={`document-group-${stageName.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}>
            <h3 className="font-display text-base font-semibold text-slate-800">{stageName}</h3>
            <div className="mt-3 divide-y divide-slate-100">
              {list.map((d) => (
                <div key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center justify-between gap-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText size={16} className="shrink-0 text-slate-400" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-800">{d.original_filename}</div>
                      <div className="text-xs text-slate-400">
                        {d.doc_type || "Other"} · {fmtSize(d.size)} · {d.uploaded_by} · {(d.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {d.access === "Restricted" && (
                      <span data-testid={`document-restricted-${d.id}`} title="Restricted access" className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        <Lock size={10} /> Restricted
                      </span>
                    )}
                    {d.my_level === "read" && (
                      <span data-testid={`document-readonly-${d.id}`} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Read only
                      </span>
                    )}
                    <button data-testid={`download-document-${d.id}`} onClick={() => download(d)} aria-label="Download document" title="Download" className="text-slate-400 transition-colors hover:text-slate-800">
                      <Download size={15} />
                    </button>
                    {d.my_level !== "read" && (
                      <button data-testid={`edit-document-${d.id}`} onClick={() => setEdit({ id: d.id, original_filename: d.original_filename, stage: d.stage || "", doc_type: d.doc_type || "" })} aria-label="Edit document details" title="Edit details" className="text-slate-400 transition-colors hover:text-slate-800">
                        <Pencil size={15} />
                      </button>
                    )}
                    {d.my_level === "full" && (
                      <button data-testid={`permissions-document-${d.id}`} onClick={() => setPerms({ ...d, access: d.access || "All", allowed: d.allowed || [] })} aria-label="Manage access" title="Manage access" className="text-slate-400 transition-colors hover:text-slate-800">
                        <Users size={15} />
                      </button>
                    )}
                    {d.my_level !== "read" && (
                      <button data-testid={`delete-document-${d.id}`} onClick={() => remove(d)} aria-label="Delete document" title="Delete" className="text-slate-400 transition-colors hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="document-edit-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
            <DialogDescription>{edit?.original_filename}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="field-label">Stage Gate</label>
              <Select testId="edit-doc-stage-select" value={edit?.stage} onChange={(v) => setEdit((p) => ({ ...p, stage: v }))} options={stages || []} placeholder="Unassigned" />
            </div>
            <div>
              <label className="field-label">Document Type</label>
              <Select testId="edit-doc-type-select" value={edit?.doc_type} onChange={(v) => setEdit((p) => ({ ...p, doc_type: v }))} options={DOC_TYPES} placeholder="Select type" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="doc-edit-cancel-btn" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="doc-edit-save-btn" onClick={saveEdit} style={{ backgroundColor: "var(--brand-primary)" }}>Save Details</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!perms} onOpenChange={(o) => !o && setPerms(null)}>
        <DialogContent data-testid="document-permissions-dialog" className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document Access</DialogTitle>
            <DialogDescription>{perms?.original_filename}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="field-label">Who can see this document</label>
              <Select
                testId="doc-access-select"
                value={perms?.access}
                onChange={(v) => setPerms((p) => ({ ...p, access: v }))}
                options={[{ value: "All", label: "Everyone in the workspace" }, { value: "Restricted", label: "Only the people I list" }]}
                placeholder="Select"
              />
            </div>
            {perms?.access === "Restricted" && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="field-label mb-0">People</label>
                  <Button size="sm" variant="outline" data-testid="add-doc-person-btn" onClick={() => setPerms((p) => ({ ...p, allowed: [...(p.allowed || []), { email: "", level: "read" }] }))} className="gap-1.5">
                    <Plus size={13} /> Add Person
                  </Button>
                </div>
                <p className="mb-2 text-xs text-slate-500">Admins, the PMO and you as the owner always keep full access.</p>
                {(perms.allowed || []).length === 0 ? (
                  <p className="rounded border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">Nobody added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {perms.allowed.map((a, i) => (
                      <div key={i} data-testid={`doc-person-row-${i}`} className="flex items-end gap-2">
                        <div className="flex-1">
                          <Input data-testid={`doc-person-email-${i}`} value={a.email} placeholder="name@company.com" onChange={(e) => setPerms((p) => ({ ...p, allowed: p.allowed.map((x, idx) => (idx === i ? { ...x, email: e.target.value } : x)) }))} />
                        </div>
                        <div className="w-52">
                          <Select testId={`doc-person-level-${i}`} value={a.level} onChange={(v) => setPerms((p) => ({ ...p, allowed: p.allowed.map((x, idx) => (idx === i ? { ...x, level: v } : x)) }))} options={LEVELS} placeholder="Level" />
                        </div>
                        <button data-testid={`remove-doc-person-${i}`} onClick={() => setPerms((p) => ({ ...p, allowed: p.allowed.filter((_, idx) => idx !== i) }))} aria-label="Remove person" title="Remove person" className="pb-2 text-slate-300 hover:text-red-600">
                          <X size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="doc-permissions-cancel-btn" onClick={() => setPerms(null)}>Cancel</Button>
            <Button data-testid="doc-permissions-save-btn" onClick={savePerms} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Lock size={14} /> Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
